// Vuex module for the authenticated User. v1 carried the small set of
// fields the original Twitch-only flow needed (`display_name`, `twitch_id`,
// `is_admin`, `loggedIn`) hydrated synchronously from localStorage.
//
// 2.7 adds `state.me` — the full UserMeResponse v2 payload from
// `GET /users/me` (shipped in 4.9). The navbar derives its display from
// `state.me` via `deriveNavbarDisplay`; the legacy fields remain populated
// so any other component that still reads them keeps working until a
// follow-up task retires the localStorage-mirror pattern.
//
// On `fetchMe` success we mirror v2 into the legacy fields too, keeping
// both views consistent. On `clearMe` (logout) everything resets.

const UserModule = {
    state() {
        return {
            // Legacy v1 fields — hydrated synchronously from localStorage so
            // `loggedIn` is correct on the very first render (no flash from
            // false → true while fetchMe is in flight).
            display_name: localStorage.display_name ?? '',
            twitch_id: localStorage.twitch_id ?? '',
            is_admin: localStorage.is_admin === 'true',
            loggedIn: localStorage.token != null,
            owned_items: [],

            // v2 (2.7): full UserMeResponse from GET /users/me. Null until
            // fetchMe resolves, and after clearMe / logout. The navbar
            // derives `display_name` from this via `deriveNavbarDisplay(me)`.
            me: null,
        };
    },
    mutations: {
        // 2.7: write the full v2 payload AND keep the legacy fields in sync
        // so consumers that haven't migrated yet still see consistent values.
        // `me.display_name` is the server's hardcoded top-level
        // (`providers.twitch.username`); legacy fields mirror that. The
        // navbar uses `deriveNavbarDisplay(me)` instead, which switches on
        // `via_provider` — see the comment in `displayName.js`.
        setMe(state, me) {
            state.me = me;
            if (me) {
                state.loggedIn = true;
                state.is_admin = me.is_admin === true;
                state.twitch_id = me.twitch_id ?? '';
                state.display_name = me.display_name ?? '';
            }
        },
        clearMe(state) {
            state.me = null;
            state.loggedIn = false;
            state.display_name = '';
            state.twitch_id = '';
            state.is_admin = false;
        },
    },
    actions: {
        // 2.7: hydrate from GET /users/me. Called by App.vue on mount.
        // Same-origin relative URL — the session cookie is host-only and
        // attaches only on requests to the same origin (see App.vue's
        // original comment; that constraint is preserved here).
        //
        // 401 / network failure: leave state alone rather than clobber it.
        // fetchMe is idempotent and may be called multiple times in a
        // session; a transient failure shouldn't log the user out.
        async fetchMe({ commit }) {
            try {
                const response = await fetch('/users/me', {
                    method: 'GET',
                    credentials: 'include',
                });
                if (!response.ok) {
                    return null;
                }
                const me = await response.json();
                // Gate on `via_provider`, NOT `me.token` — the top-level
                // `token` field is explicitly marked transitional in
                // UserMeResponse.ts and is slated for removal once App.vue's
                // localStorage hydration is migrated off `providers.*`.
                // Gating on a soon-removable field would silently break
                // hydration the moment that cleanup ships. `via_provider`
                // is a core v2 invariant (server-default 'twitch' for legacy
                // tokens, never absent) and the right sentinel here.
                if (!me || !me.via_provider) {
                    return null;
                }
                // Keep the localStorage mirror behavior alive — App.vue's
                // original `created()` hook set these so the rest of the
                // codebase (router.js admin gate via localStorage.is_admin,
                // Authorization-header consumers via localStorage.token)
                // keeps working. A follow-up task migrates those onto
                // Vuex; until then, fetchMe owns the mirror.
                localStorage.token = me.token;
                localStorage.display_name = me.display_name ?? '';
                localStorage.twitch_id = me.twitch_id ?? '';
                localStorage.is_admin = me.is_admin === true ? 'true' : 'false';
                commit('setMe', me);
                return me;
            } catch (e) {
                // Network error / SPA loaded without a server — stay logged
                // out. Match App.vue's original behavior.
                console.log('users/me hydration skipped:', e);
                return null;
            }
        },
        clearMe({ commit }) {
            commit('clearMe');
        },
    },
};

export default UserModule;
